import { useTranslation } from 'react-i18next';
import { Check, Image as ImageIcon } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import {
  MEAL_THUMB_PX,
  useMealThumbnailSize,
  type MealThumbnailSize,
} from '@/hooks/useMealThumbnailSize';
import { cn } from '@/lib/utils';

/**
 * Profile-page control for the food-log meal-photo thumbnail size. Mirrors
 * `ThemeSwitcher` — applies immediately, persists to this browser. Each tile
 * previews the actual size it sets.
 */
const OPTIONS: Array<{ value: MealThumbnailSize; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

export const MealThumbnailSizeSwitcher = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [size, setSize] = useMealThumbnailSize();

  return (
    <PrefSection
      variant={variant}
      icon={ImageIcon}
      title={t('Meal photo size')}
      description={t('Size of meal photos in the food log. Saved to this browser.')}
    >
      <div className="grid gap-2 sm:grid-cols-3">
          {OPTIONS.map((o) => {
            const active = size === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setSize(o.value)}
                aria-pressed={active}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-md border p-3 transition-colors',
                  active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
                )}
              >
                <span className="flex h-14 items-center">
                  <span
                    aria-hidden
                    className="rounded border bg-muted/50"
                    // Scaled-down preview — the real thumbnails are large, so a
                    // literal swatch would dominate the card.
                    style={{
                      width: Math.round(MEAL_THUMB_PX[o.value] * 0.375),
                      height: Math.round(MEAL_THUMB_PX[o.value] * 0.375),
                    }}
                  />
                </span>
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  {t(o.label)}
                  {active && <Check className="h-3.5 w-3.5 text-primary" />}
                </span>
              </button>
            );
          })}
      </div>
    </PrefSection>
  );
};
